import React from 'react';
import { Box, Typography } from '@mui/joy';

export const markdownComponents = {
  h1: ({ children }) => (
    <Typography
      level="h1"
      sx={{
        color: 'text.primary',
        fontSize: { xs: '1.75rem', md: '2rem' },
        fontWeight: 700,
        mt: 5,
        mb: 2.5,
      }}
    >
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography
      level="h2"
      sx={{
        color: 'text.primary',
        fontSize: { xs: '1.5rem', md: '1.75rem' },
        fontWeight: 700,
        mt: 5,
        mb: 2.5,
      }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography
      level="h3"
      sx={{
        color: 'text.primary',
        fontSize: { xs: '1.2rem', md: '1.35rem' },
        fontWeight: 600,
        mt: 4,
        mb: 2,
      }}
    >
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography
      level="h4"
      sx={{
        color: 'text.primary',
        fontSize: '1.1rem',
        fontWeight: 600,
        mt: 3,
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  ),
  h5: ({ children }) => (
    <Typography
      level="h5"
      sx={{
        color: 'text.primary',
        fontSize: '1rem',
        fontWeight: 600,
        mt: 3,
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  ),
  h6: ({ children }) => (
    <Typography
      level="h6"
      sx={{
        color: 'text.secondary',
        fontSize: '0.875rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        mt: 2,
        mb: 1,
      }}
    >
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography
      component="p"
      sx={{
        color: 'text.secondary',
        fontSize: '1rem',
        lineHeight: 1.7,
        mb: 2,
      }}
    >
      {children}
    </Typography>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: '#2E5BBA', textDecoration: 'none' }}
      onMouseEnter={(e) => { e.target.style.textDecoration = 'underline'; }}
      onMouseLeave={(e) => { e.target.style.textDecoration = 'none'; }}
    >
      {children}
    </a>
  ),
  ul: ({ children }) => (
    <Box
      component="ul"
      sx={{ color: 'text.secondary', pl: 3, mb: 2, lineHeight: 1.7 }}
    >
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box
      component="ol"
      sx={{ color: 'text.secondary', pl: 3, mb: 2, lineHeight: 1.7 }}
    >
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box
      component="li"
      sx={{ mb: 0.5, color: 'text.secondary' }}
    >
      {children}
    </Box>
  ),
  blockquote: ({ children }) => (
    <Box
      component="blockquote"
      sx={{
        borderLeft: '4px solid #2E5BBA',
        pl: 3, py: 2, my: 3,
        fontStyle: 'italic',
        background: 'rgba(46, 91, 186, 0.05)',
        borderRadius: '0 8px 8px 0',
        color: 'text.secondary',
      }}
    >
      {children}
    </Box>
  ),
  code: ({ children, className }) => {
    const isBlock = className && className.startsWith('language-');
    if (isBlock) {
      return (
        <Box
          component="code"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.9em',
          }}
        >
          {children}
        </Box>
      );
    }
    return (
      <Box
        component="code"
        sx={{
          background: 'rgba(255, 255, 255, 0.1)',
          px: 0.8,
          py: 0.3,
          borderRadius: 1,
          fontFamily: 'monospace',
          fontSize: '0.9em',
          color: 'text.primary',
        }}
      >
        {children}
      </Box>
    );
  },
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        background: 'rgba(0, 0, 0, 0.4)',
        p: 3, borderRadius: 2, overflowX: 'auto',
        fontFamily: 'monospace', fontSize: '0.9rem',
        lineHeight: 1.6, mb: 3,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        color: 'text.primary',
      }}
    >
      {children}
    </Box>
  ),
  strong: ({ children }) => (
    <Box component="strong" sx={{ color: 'text.primary', fontWeight: 600 }}>
      {children}
    </Box>
  ),
  em: ({ children }) => (
    <Box
      component="em"
      sx={{ color: 'text.secondary', fontStyle: 'italic' }}
    >
      {children}
    </Box>
  ),
  del: ({ children }) => (
    <Box
      component="del"
      sx={{
        color: 'text.secondary',
        textDecoration: 'line-through',
        opacity: 0.7,
      }}
    >
      {children}
    </Box>
  ),
  hr: () => (
    <Box
      component="hr"
      sx={{
        border: 'none',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        my: 4,
      }}
    />
  ),
  img: ({ src, alt }) => (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        maxWidth: '100%',
        height: 'auto',
        borderRadius: 2,
        my: 2,
        display: 'block',
      }}
    />
  ),
  table: ({ children }) => (
    <Box
      sx={{
        overflowX: 'auto',
        my: 3,
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 2,
        background: 'rgba(255, 255, 255, 0.03)',
      }}
    >
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.95rem',
        }}
      >
        {children}
      </Box>
    </Box>
  ),
  thead: ({ children }) => (
    <Box
      component="thead"
      sx={{
        background: 'rgba(46, 91, 186, 0.1)',
        color: 'text.primary',
      }}
    >
      {children}
    </Box>
  ),
  tbody: ({ children }) => (
    <Box component="tbody" sx={{ color: 'text.secondary' }}>
      {children}
    </Box>
  ),
  tr: ({ children }) => (
    <Box
      component="tr"
      sx={{
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      {children}
    </Box>
  ),
  th: ({ children }) => (
    <Box
      component="th"
      sx={{
        textAlign: 'left',
        p: 1.5,
        fontWeight: 700,
        color: 'text.primary',
      }}
    >
      {children}
    </Box>
  ),
  td: ({ children }) => (
    <Box
      component="td"
      sx={{
        p: 1.5,
        color: 'text.secondary',
      }}
    >
      {children}
    </Box>
  ),
  input: ({ type, checked, disabled }) => {
    if (type === 'checkbox' || type === 'checkbox') {
      return (
        <Box
          component="input"
          type="checkbox"
          checked={checked}
          disabled={disabled}
          readOnly
          sx={{
            mr: 1,
            accentColor: '#2E5BBA',
          }}
        />
      );
    }
    return null;
  },
};

export default markdownComponents;
